// PORTED FROM VUE-UI + ADAPTED TO MAISON

import { debounce, cloneDeep } from 'lodash';
import { Component, Prop, Ref, Watch } from 'vue-property-decorator';
import AAbstractInput from '@/components/AAbstractInput';
import { Model } from '@/models/Model';

@Component<ASelectInput>({})
export default class ASelectInput extends AAbstractInput {
  // #region @Props
  @Prop() protected model!: Model;

  /**
   * when you don't want to use model and just have a simple list of options
   *   , you can use the items -> item-text and item-search should still work!
   */
  @Prop() protected items!: any[];

  @Prop() protected noResultText!: string;

  /** Name of API filter parameter: e.g. filters[search]={query}  */
  @Prop({ default: 'search' }) protected itemSearch!: string;

  /**
   * when you have a default value(s), you want to preload the values.
   * For this we can do a special initial call to server
   * Defaults to id which results in /users?filters[id]={id},{id}
   */
  @Prop({ default: 'id' }) protected itemValueFilter!: string;

  @Prop({ default: 15 }) protected searchLimit!: number;

  @Prop({ default: false }) protected hideDetails!: boolean;

  @Prop({default :false}) protected alwaysPreloadMultiple!: boolean;

  @Prop({ default: false }) protected hideLabel!: boolean;
  // #endregion

  // #region @Refs
  @Ref() readonly vAutocomplete!: HTMLFormElement;
  // #endregion

  // #region Class properties
  protected internalItems: AInputableSelect[] = [];

  protected doSearch = debounce(this.handleDoSearch, 300);

  protected isLoading = false;

  /** used with cachesItems */
  protected loadedCachedItems = false;

  protected searchString = '';
  // #endregion

  // #region Lifecycle Hooks / Init
  public async created(): Promise<void> {
    this.onCreated();
  }

  public async onCreated(): Promise<void> {
    if (this.canSelectMultiple() && this.internalValue !== null && ! Array.isArray(this.internalValue)) {
      throw new Error('You cannot set a non-array v-model for <ASelectInput> when you have the `multiple` property set');
    }
    if (this.hasModel() && ! this.noInitialValues()) {
      this.loadCurrentSelection();
    } else {
      this.internalItems = this.items || [];
    }
  }

  /**
   * Initial value can be number/string/objects or array of previous values
   * When we search via API we want to preload ONLY those values
   */
  public async loadCurrentSelection(): Promise<void> {
    if (! this.internalValue) {
      return;
    }

    if (! Array.isArray(this.internalValue)) {
      this.preloadSingleValue();
      return;
    }

    this.preloadMultipleValues();
  }
  // #endregion

  // #region Class methods
  public handleDoSearch(query: string|null): void {
    query = query || '';

    this.searchModels(query);
  }

  protected loadOnFocus(): void {
    if (! this.hasModel()) {
      return;
    }
    // when value already set we still want to load all items initially
    if (this.cachesItems || ! this.loadedCachedItems) {
      this.loadedCachedItems = true;
      this.searchModels(''); // don't use searchString since that has the default initial value
    } else if (! this.searchString) {
      // when we focus and have not yet searched, we want results immediately
      this.searchModels(this.searchString);
    }
  }

  private canSelectMultiple(): boolean {
    return this.$attrs.multiple !== undefined;
  }

  private queryModel(): Model {
    return cloneDeep(this.model);
  }

  protected handleChangeEvent(): void {
    if (this.canSelectMultiple()) {
      return;
    }
    this.vAutocomplete.blur();
  }

  protected get filterLocally(): boolean {
    return this.hasModel() && ! this.cachesItems;
  }

  /**
   * Usefull to enable cache-items when you only have a few results which you only
   *  need to retreive once
   */
  protected get cachesItems(): boolean {
    if (typeof this.$attrs['cache-items'] === 'boolean') {
      return this.$attrs['cache-items'];
    }
    return this.$attrs['cache-items'] !== undefined;
  }

  protected get attrItemText(): string {
    return this.$attrs['item-text'] ? this.$attrs['item-text'] : 'label';
  }

  protected handleBlurEvent(): void {
    if (this.canSelectMultiple()) {
      this.loadCurrentSelection();
    }
  }

  private noInitialValues(): boolean {
    return ! this.internalValue
    || (Array.isArray(this.internalValue) && this.internalValue.length === 0);
  }

  private selectedModelAlreadyLoaded(): boolean {
    if (this.noInitialValues()) {
      return false;
    }

    let modelAlreadyLoaded = false;
    // do any here because gives nasty warning... AInputableSelect
    this.internalItems.forEach((item: any) => {
      if (typeof item === 'object' && item[`${this.itemValueKey}`] === this.internalValue[`${this.itemValueKey}`]) {
        modelAlreadyLoaded = true;
      }
    });

    return modelAlreadyLoaded;
  }

  private async preloadSingleValue(): Promise<void> {
    if (this.internalValue && typeof this.internalValue === 'object') {
      if (! this.selectedModelAlreadyLoaded()) {
        this.internalItems.push(this.internalValue);
      }
      return;
    }

    if (! this.hasModel()) {
      return;
    }

    const response = await this.queryModel().filter({
      [this.itemValueFilter]: this.internalValue,
    }).first();

    if (response) {
      this.internalItems.push(response);
    }
  }

  /**
   * Only attempt to load data of models from basic values
   * When you alreayd have a model or object you can just set these values
   */
  private async preloadMultipleValues(): Promise<void> {
    if (this.internalItems.length && ! this.alwaysPreloadMultiple) {
      return;
    }

    const initialValues: AInputableSelect[] = [];
    const preloadValueIds: Array<number | string | boolean> = [];
    this.internalValue.forEach((item: AInputableSelect) => {
      if (typeof item === 'object') {
        initialValues.push(item);
      } else {
        preloadValueIds.push(item);
      }
    });

    let preloadedValues: Model[] = [];
    if (preloadValueIds.length > 0 && this.hasModel()) {
      const response = await this.queryModel().filter({
        [this.itemValueFilter]: preloadValueIds,
      }).all();
      if (response) {
        preloadedValues = response;
      }
    }

    this.internalItems = [...initialValues, ...preloadedValues];
  }

  protected valueDifferent(query: string): boolean {
    if (! this.internalValue) {
      return true;
    }

    if (typeof this.internalValue === 'string' && query === this.internalValue) {
      return false;
    }

    if (
      typeof this.internalValue === 'object'
      && this.internalValue[`${this.attrItemText}`] === query
    ) {
      return false;
    }

    return true;
  }

  protected hasModel(): boolean {
    return this.model !== undefined;
  }

  protected onBottomOfItemListVisibile(entries: boolean): void {
    // Early return if
    if (! entries) { return; } // bottom not reached

    this.searchModelsForNextPage();
  }

  public async searchModelsForNextPage(): Promise<void> {
    // Early return if
    if (
      this.isLoading // still loading, or
      || ! this.lastInternalItemHasNextPage // there's no next page
    ) {
      return;
    }

    if (this.hasModel()) {
      this.isLoading = true;
      this.internalItems = [
        ...this.internalItems,
        ...await cloneDeep(this.internalItems[this.internalItems.length -1] as Model)
          .limit(this.searchLimit)
          .page((this.internalItems[this.internalItems.length -1] as any).meta.current_page + 1)
          .all()
      ];
      this.isLoading = false;
    }
  }

  protected get lastInternalItemHasNextPage(): boolean {
    // Early return if
    if (
      ! this.hasModel() // not bound to a model, or
      || ! this.internalItems.length // there's no results
    ) {
      return false;
    }

    const lastInternalItem = this.internalItems[this.internalItems.length -1] as {
      meta?: {
        last_page?: number;
        current_page?: number;
      }
    };

    // Sidenote: This could be shortened to
    // lastInternalItem.meta?.current_page === lastInternalItem.meta?.last_page
    // if the linter supports it
    //
    // Early return if
    if (
      ! lastInternalItem.meta
      || ! lastInternalItem.meta.last_page
      || ! lastInternalItem.meta.current_page
      || lastInternalItem.meta.current_page === lastInternalItem.meta.last_page
    ) {
      return false;
    }

    return true;
  }

  protected get returnsObject(): boolean {
    return ! this.$attrs['item-value'] || (
      this.$attrs['return-object'] !== undefined &&
      this.$attrs['return-object'] !== null &&
      (this.$attrs['return-object'] as unknown as boolean) !== false
    );
  }

  protected get itemValueKey(): string {
    return this.$attrs['item-value'] ? this.$attrs['item-value'] : 'id';
  }

  protected get hasDeletableChips(): boolean {
    if (typeof this.$attrs.disabled === 'string') {
      return false;
    }

    if (this.$attrs['deletable-chips']) {
      return !! this.$attrs['deletable-chips'];
    }

    if (typeof this.$attrs.chips === 'string') {
      return true;
    }

    return false;
  }
  // #endregion

  // #region Async methods
  public async searchModels(query: string): Promise<void> {
    if (this.hasModel()) {
      this.isLoading = true;
      this.internalItems = await this.queryModel()
        .filter({ [this.itemSearch]: query })
        .limit(this.searchLimit)
        .all();
      this.isLoading = false;
    }
  }
  // #endregion

  // #region @Watchers
  @Watch('internalValue')
  public onChangedInternalValue(newValue: unknown, oldValue: unknown): void {
    // eslint-disable-next-line eqeqeq
    if (oldValue == newValue) return;

    this.$emit('input', newValue);

    this.loadCurrentSelection();
  }

  @Watch('$attrs.value')
  public onChangedValue(newValue: unknown, oldValue: unknown): void {
    // eslint-disable-next-line eqeqeq
    if (oldValue == newValue) return;
    this.internalValue = newValue;
  }

  @Watch('searchString')
  protected searchChanged(query: string|null): void {
    if (query === null || query === undefined || this.cachesItems) {
      return;
    }

    if (this.valueDifferent(query)) {
      this.doSearch(query);
    }
  }

  // @Watch('searchString')
  // protected searchChanged(query: string|undefined|null): void {
  //   if (query === null || query === undefined) {
  //     return;
  //   }

  //   if (! this.cachesItems) {
  //     this.doSearch(query);
  //   }
  // }

  // #endregion
}

export type AInputableSelect = Model | Record<string, unknown> | string | number | boolean
